/* eslint-disable import/no-unresolved */
/**
 * Rutas del shell privado v3: un `Route` por cada path registrado en `V3_PRIVATE_SHELL_PATH_MATCHERS`
 * (React Router 4.3 solo acepta `path` string) + un `PrivateLayout` estable para todas las URLs del
 * shell cuando `uxDashboardImprovements` está activo. Así el menú / flyout no se desmonta al navegar
 * (p. ej. agenda ↔ transferencias ↔ tarjeta corporativa) y la animación del submenú es coherente.
 *
 * Con UX dashboard **off** este módulo no registra rutas (`[]`); `PrivateRoutes` cubre legacy.
 *
 * Para sumar pantallas: extender `v3PrivateShellRegistry.js` y el `Switch` interno.
 */
import React from "react";
import { shape, string, bool } from "prop-types";
import { connect } from "react-redux";
import { Route, Redirect, Switch, withRouter } from "react-router-dom";

import * as config from "util/config";
import { getDisplay } from "util/device";

import { CORPORATE_GROUP_ENVIRONMENT_TYPE, ENVIRONMENT_TYPE } from "constants.js";
import { getEcomexPrimaryPath } from "v3/layout/buildPrivateNavItems";
import { selectors as configSelectors } from "reducers/config";
import { selectors as sessionSelectors } from "reducers/session";
import {
    getPostLoginHomePath,
    getV3PrivateNavAccountsPath,
    getTransferExecutePath,
    getTransferTicketPath,
    TRANSFER_CONFIRMATION_LEGACY_PATH,
    TRANSFER_DOWNLOAD_V3_PATH,
    TRANSFER_HISTORIC_V3_PATH,
} from "util/v3UxConfig";
import {
    isCredinV3Enabled,
    isTransferHistoricV3Enabled,
    isUxMetrocorpImprovementsEnabled,
    isUxMyAccountsImprovementsEnabled,
} from "util/v3ShellLayoutSync";
import {
    matchesV3PrivateShellPath,
    V3_PRIVATE_SHELL_FREQUENT_PATH_RULES,
    V3_PRIVATE_SHELL_PATH_MATCHERS,
} from "routes/v3PrivateShellRegistry";
import buildV3EcheqsShellRoutes from "routes/v3EcheqsShellRoutes";
import AccountStatementsPage from "pages/v2/corporateCard/AccountStatementsPage";
import V3PrivateShellRoute from "v3/routes/V3PrivateShellRoute";
import DashboardV3 from "v3/pages/dashboard/DashboardV3";
import Agenda from "pages/v2/agenda/Agenda";
import CreateContactPage from "pages/v2/agenda/CreateContact";
import FrequentDestinationCreate from "pages/frequentDestinations/CreateFrequentDestination";
import FrequentDestinationCreateConfirmation from "pages/frequentDestinations/_components/CreateFrequentDestinationConfirmations";
import FrequentDestinationCreateTransfer from "pages/frequentDestinations/CreateFrequentDestinationTransfer";
import FrequentDestinationDelete from "pages/frequentDestinations/DeleteConfirmation";
import FrequentDestinationDetail from "pages/frequentDestinations/Details";
import FrequentDestinationList from "pages/frequentDestinations/List";
import ModifyFrequentDestination from "pages/frequentDestinations/ModifyFrequentDestination";
import ModifyFrequentDestinationConfirmations from "pages/frequentDestinations/_components/ModifyFrequentDestinationConfirmations";
import TransferHistoric from "v3/pages/transfers/TransferHistoric";
import ExecuteTransferV3Page from "v3/pages/transfers/ExecuteTransferV3Page";
import TransferDownloadV3Page from "v3/pages/transfers/TransferDownloadV3Page";
import TransferConfirmationV3Page from "v3/pages/transfers/TransferConfirmationV3Page";
import TransferTicketV3Page from "v3/pages/transfers/TransferTicketV3Page";
import TransferDownload from "pages/v2/transfer/TransferDownload";
import V2Transfer from "pages/v2/transfer/V2Transfer";
import MultipleTransfer from "pages/transfer/multiple/MultipleTransfer";
import MultipleTransferDashboard from "pages/transfer/multiple/MultipleTransferDashboard";
import MultipleTransferDetail from "pages/transfer/multiple/MultipleTransferDetail";
import MultipleTransferSummary from "pages/transfer/multiple/MultipleTransferSummary";
import MultipleTransferTicket from "pages/transfer/multiple/MultipleTransferTicket";
import PendingTransactionList from "pages/transactions/PendingTransactionList";
import MultipleSignaturesSummary from "pages/transactions/MultipleSignaturesSummary";
import MultipleSignaturesDelete from "pages/transactions/MultipleSignaturesDelete";
import ProcessingMultipleSignatures from "pages/transactions/ProcessingMultipleSignatures";
import ProcessingMultipleDelete from "pages/transactions/ProcessingMultipleDelete";
import AccountDetail from "pages/accounts/Details";
import AccountOpening from "pages/accounts/AccountOpening";
import AccountOpeningSummary from "pages/accounts/AccountOpeningSummary";
import AccountOpeningTicket from "pages/accounts/AccountOpeningTicket";
import AccountsV2Page from "pages/v2/accounts/AccountsV2Page";
import AccountsV3Page from "v3/pages/accounts/AccountsV3Page";
import AccountDetailV3Page from "v3/pages/accounts/AccountDetailV3Page";
import AccountProjectedBalanceV3Page from "v3/pages/accounts/AccountProjectedBalanceV3Page";
import AccountHistoricBalanceV3Page from "v3/pages/accounts/AccountHistoricBalanceV3Page";
import AccountCreateV3Page from "v3/pages/accounts/AccountCreateV3Page";
import AccountOpeningSummaryV3Page from "v3/pages/accounts/AccountOpeningSummaryV3Page";
import AccountTicketV3Page from "v3/pages/accounts/AccountTicketV3Page";
import AccountsOfficer from "pages/settings/AccountsOfficer";
import Settings from "pages/settings/Settings";
import ChangePassword from "pages/settings/ChangePassword";
import ChangeUserpass from "pages/settings/ChangeUserpass";
import ChangeSecuritySeal from "pages/settings/ChangeSecuritySeal";
import ChangeSecuritySealConfirmation from "pages/settings/ChangeSecuritySealConfirmation";
import NotificationsConfiguration from "pages/settings/NotificationsConfiguration";
import NotificationsConfigurationTransports from "pages/settings/NotificationsConfigurationTransports";
import FingerprintConfiguration from "pages/settings/FingerprintConfiguration";
import FingerprintDeleteConfirmation from "pages/settings/FingerprintDeleteConfirmation";
import ListDevices from "pages/settings/pushNotifications/ListDevices";
import MyDevices from "pages/settings/MyDevices";
import Token from "pages/settings/Token";
import ChangeEmail from "pages/settings/ChangeEmail";
import ChangeEmailConfirmation from "pages/settings/ChangeEmailConfirmation";
import ChangeAddresses from "pages/settings/ChangeAddresses";
import ChangePhone from "pages/settings/ChangePhone";
import ChangePhoneConfirmation from "pages/settings/ChangePhoneConfirmation";
import SetDefaultEnvironment from "pages/settings/SetDefaultEnvironment";
import ChangeEnvironment from "pages/settings/ChangeEnvironment";
import ChangeLanguage from "pages/settings/ChangeLanguage";
import LastLogin from "pages/settings/LastLogin";
import QAlertConfiguration from "pages/settings/pushNotifications/QAlertConfiguration";
import DocumentsIndex from "pages/settings/_esign/DocumentsIndex";
import EsignDocumentsSigned from "pages/settings/_esign/EsignDocumentsSigned";
import PointsOfInterest from "pages/pointsOfInterest";
import TokenPassword from "pages/settings/TokenPassword";
import TokenRandomCode from "pages/settings/TokenRandomCode";
import TokenValidateRandomCode from "pages/settings/TokenValidateRandomCode";
import ChangeTokenPass from "pages/settings/_components/token/ChangeTokenPass";
import CodeActivationMobile from "pages/settings/_components/token/CodeActivationMobile";
import CodeActivationPasswordMobile from "pages/settings/_components/token/CodeAtivationPasswordMobile";
import DisabledTokenPass from "pages/settings/_components/token/DisabledTokenPass";
import RecoverToken from "pages/settings/_components/token/RecoverTokenPassword";
import TokenDrawer from "pages/settings/_components/token/TokenDrawer";
import ActivateTokenWithBiometrics from "pages/v2/transfer/settings/ActivateTokenWithBiometrics";
import DisableBiometricToken from "pages/v2/transfer/settings/DisableBiometricToken";
import TokenSeedError from "pages/v2/transfer/settings/TokenSeedError";
import HideBalancePage from "pages/v2/transfer/settings/HideBalancePage";
import ProyectedBalance from "pages/accounts/ProyectedBalance";
import HistoricBalance from "pages/accounts/HistoricBalance";
import ConsolidatedPosition from "pages/ConsolidatedPosition/ConsolidatedPosition";
import TransferError from "pages/transfer/TransferError";
import TransferScheduled from "pages/transfer/TransferScheduled";
import TransferScheduledCancel from "pages/transfer/TransferScheduledCancel";
import TransferSummary from "pages/transfer/TransferSummary";
import TransferTransaction from "pages/transfer/TransferTransaction";
import Transaction from "pages/forms/Transaction";
import TransactionPaymentLines from "pages/forms/TransactionPaymentLines";
import RequestCredin from "pages/transfer/RequestCredin";
import CreateCredinV3Page from "v3/pages/transfers/CreateCredinV3Page";
import GenerateCredinV3Page from "v3/pages/transfers/GenerateCredinV3Page";
import CredinTicketV3Page from "v3/pages/transfers/CredinTicketV3Page";
import GenerateCredin from "pages/transfer/GenerateCredin";
import CredinPrepareConfirmation from "pages/transfer/CredinPrepareConfirmation";
import IncomingDebinList from "pages/debinPayments/IncomingDebinList";
import IncomingDebinTransaction from "pages/debinPayments/IncomingDebinTransaction";
import IncomingDebinDetail from "pages/debinPayments/_components/IncomingDebinDetail";
import AcceptDebinConfirm from "pages/debinPayments/AcceptDebinConfirm";
import DeclineDebinConfirm from "pages/debinPayments/DeclineDebinConfirm";
import ChargebackDebinConfirm from "pages/debinPayments/ChargebackDebinConfirm";
import ChargebackPullConfirm from "pages/debinPayments/ChargebackPullConfirm";
import FixedTermAcceptance from "pages/fixedTermAcceptance/FixedTermAcceptance";
import FixedTermAcceptanceDetail from "pages/fixedTermAcceptance/_components/FixedTermAcceptanceDetail";
import AcceptFixedTermConfirm from "pages/fixedTermAcceptance/AcceptFixedTermConfirm";
import DeclineFixedTermConfirm from "pages/fixedTermAcceptance/DeclineFixedTermConfirm";
import RecurrentDebins from "pages/debinPayments/_components/recurrentDebin/RecurrentDebins";
import LoadDebinRecurrency from "pages/debinPayments/_components/recurrentDebin/LoadDebinRecurrency";
import ConfirmRecurrencyDebin from "pages/debinPayments/_components/recurrentDebin/ConfirmRecurrencyDebin";
import RecurrencyDetail from "pages/debinPayments/_components/recurrentDebin/RecurrencyDetail";
import PrestacionTransaction from "pages/debinPayments/_components/recurrentDebin/PrestacionTransaction";
import RecurrencyTransaction from "pages/debinPayments/_components/recurrentDebin/RecurrencyTransaction";
import ConfirmRecurrencyDebinAction from "pages/debinPayments/_components/recurrentDebin/ConfirmRecurrencyDebinAction";
import ProgrammedDebin from "pages/v2/debin/scheduled/ProgrammedDebin";
import DebinScheduledDetail from "pages/v2/debin/scheduled/DebinScheduledDetail";
import DebinScheduledConfirmationPage from "pages/v2/debin/scheduled/DebinScheduledConfirmationPage";
import AddLimitRequest from "pages/v2/addlimitrequest/AddLimitRequest";
import AddLimitRequestConfirmation from "pages/addlimitrequest/AddLimitRequestConfirmation";
import AddLimitTicket from "pages/temporarylimits/AddLimitTicket";
import PayThirdPartiesList from "pages/payThirdParties/List";
import NewPayment from "pages/payThirdParties/newPayment";
import NewPaymentSummary from "pages/payThirdParties/newPaymenSummary";
import ThirdPaymentTicket from "pages/payThirdParties/ThirdPaymentTicket";
import ThirdPaymentDetail from "pages/payThirdParties/ThirdPaymentDetail";
import TransactionTicketCancelConfirmation from "pages/forms/_components/TransactionTicketCancelConfirmation";
import TransactionTicketSignConfirmation from "pages/forms/_components/TransactionTicketSignConfirmation";
import ExternalTransaction from "pages/transactions/ExternalTransaction";
import TransactionsList from "pages/transactions/TransactionsList";
import TransactionsListFilters from "pages/transactions/_components/MobileFilters";
import TransactionsFiltersResults from "pages/transactions/_components/MobileFiltersResults";
import ServicePaymentsMain from "pages/servicePayments/ServicePaymentsMain";
import PaymentsServicesArca from "pages/v2/payments/PaymentsServicesArca";
import PaymentServicesArcaConfirmationPage from "pages/v2/payments/PaymentServicesArcaConfirmationPage";
import ServiceNewPayment from "pages/servicePayments/ServiceNewPayment";
import ServicePaymentsNewPaymentTicket from "pages/servicePayments/NewPaymentTicket";
import ServicePaymentsConfirmationTicket from "pages/servicePayments/ServicePaymentsConfirmationTicket";
import AgendaServicePaymentsConfirmationTicket from "pages/servicePayments/AgendaServicePaymentsConfirmationTicket";
import AdhesionesList from "pages/servicePayments/AdhesionesList";
import AgendaPayment from "pages/servicePayments/AgendaPayment";
import MultipleAFIPSummary from "pages/servicePayments/multiple/MultipleAFIPSummary";
import MultipleAFIPDetail from "pages/servicePayments/multiple/MultipleAFIPDetail";
import MultipleAFIPSuccess from "pages/servicePayments/multiple/MultipleAFIPSuccess";
import MultiplePaymentsConfirmation from "pages/servicePayments/multiple/MultiplePaymentsConfirmation";
import MultiplePaymentsSuccess from "pages/servicePayments/multiple/MultiplePaymentsSuccess";
import MultiplePaymentAgendaDetail from "pages/servicePayments/multiple/MultiplePaymentAgendaDetail";
import PayPaymentAFIPSummary from "pages/servicePayments/PayPaymentAFIPSummary";
import PayPaymentAFIPConfirmation from "pages/servicePayments/PayPaymentAFIPConfirmation";
import PayPaymentAFIPTicket from "pages/servicePayments/tickets/PayPaymentAFIPTicket";
import DeletePaymentAFIPTicket from "pages/servicePayments/tickets/DeletePaymentAFIPTicket";
import UnattachServiceConfirmationTicket from "pages/servicePayments/UnattachServiceConfirmationTicket";
import DeletePaymentAFIPSummary from "pages/servicePayments/DeletePaymentAFIPSummary";
import CommunicationMain from "pages/communications/Main";
import NewMessage from "pages/communications/NewMessage";
import ReadMessage from "pages/communications/ReadMessage";
import ReplyMessage from "pages/communications/ReplyMessage";
import EnabledContributorsList from "pages/enabledContributors/EnabledContributorsList";
import EcheqsControl from "pages/v2/echeqs/EcheqsControl";
import EcheqsMassManageList, {
    canAccessMassEcheqManagementList,
    canCreateMassEcheqManagementBatch,
} from "pages/v2/echeqMassManagement/EcheqsMassManageList";
import EcheqsMassManageForm from "pages/v2/echeqMassManagement/EcheqsMassManageForm";
import EcheqsMassManageConfirm from "pages/v2/echeqMassManagement/EcheqsMassManageConfirm";
import EcheqsMassManageTicket from "pages/v2/echeqMassManagement/EcheqsMassManageTicket";
import Checks from "pages/checks/Checks";
import ChecksSummary from "pages/checks/ChecksSummary";
import CheckTransaction from "pages/checks/CheckTransaction";
import ChecksDetail from "pages/checks/ChecksDetail";
import ChecksDetailInfo from "pages/checks/ChecksDetailInfo";
import DiscountedChecksDetail from "pages/checks/DiscountedChecksDetail";
import DiscountedChecksDetailInfo from "pages/checks/DiscountedChecksDetailInfo";
import EcheqRulesMain from "pages/checks/echeqRules/EcheqRulesMain";
import EcheqRuleSummary from "pages/checks/echeqRules/EcheqRuleSummary";
import CreateEcheqRule from "pages/v2/echeq/CreateEcheqRule";
import CreateEcheqRuleConfirmation from "pages/v2/echeq/CreateEcheqRuleConfirmation";
import CreateEcheqRuleTransaction from "pages/v2/echeq/CreateEcheqRuleTransaction";
import MetrocorpMain from "pages/metrocorp/MetrocorpMain";
import MetrocorpOperationsV3Page from "v3/pages/metrocorp/MetrocorpOperationsV3Page";
import Deposits from "pages/deposits/Deposits";
import DepositDetails from "pages/deposits/Details";
import DepositConstitute from "pages/deposits/DepositConstitute";
import DepositsConstituteV2 from "pages/v2/deposits/DepositsConstituteV2";
import DepositSummary from "pages/deposits/DepositSummary";
import DepositConstituteTicket from "pages/deposits/tickets/DepositConstituteTicket";
import SetProductAlias from "pages/accounts/SetProductAlias";
import FinancingListCategories from "pages/financing/FinancingListCategories";
import FinancingId from "pages/financing/_components/FinancingId";
import FinancingDetail from "pages/financing/_components/FinancingDetail";
import FeedsDetail from "pages/financing/_components/FeedsDetail";
import EcomexOperationsMain from "pages/ecomex/EcomexOperationsMain";
import EcomexInformativeRegimeMain from "pages/ecomex/EcomexInformativeRegimeMain";
import EcomexAlertsAndDuesMain from "pages/ecomex/EcomexAlertsAndDuesMain";
import EcomexNewRequests from "pages/ecomex/iframe/EcomexNewRequests";
import EcomexRequestHistory from "pages/ecomex/iframe/EcomexRequestHistory";
import EcomexConfirmTransaction from "pages/ecomex/EcomexConfirmTransaction";
import ExcomexTransactionSuccess from "pages/ecomex/EcomexTransactionSucess";
import EcomexTransaction from "pages/ecomex/EcomexTransaction";
import CurrencyExchangePage from "pages/v2/investment/currencyExchange/CurrencyExchangePage";
import CurrencyExchangeConfirmPage from "pages/v2/investment/currencyExchange/CurrencyExchangeConfirmPage";
import CurrencyExchangeTicket from "pages/v2/investment/currencyExchange/CurrencyExchangeTicket";
import CedipList from "pages/cedip/CedipList";
import CedipDetail from "pages/cedip/CedipDetail";
import AcceptOrRejectCedip from "pages/cedip/AcceptOrRejectCedip";
import RejectCedipTicket from "pages/cedip/_components/RejectCedipTicket";
import FundcorpInformationMain from "pages/fundcorp/FundcorpInformationMain";
import FundcorpOperationsMain from "pages/fundcorp/FundcorpOperationsMain";
import CancelTicket from "pages/fundcorp/_components/CancelTicket";
import FundCancelSummary from "pages/fundcorp/_components/FundCancelSummary";
import FundRegisterShareAccount from "pages/fundcorp/_components/FundRegisterShareAccount";
import FundRescue from "pages/fundcorp/_components/FundRescue";
import FundRescueSummary from "pages/fundcorp/_components/FundRescueSummary";
import FundScheduledCancel from "pages/fundcorp/_components/FundScheduledCancel";
import FundSubscribe from "pages/fundcorp/_components/FundSubscribe";
import InvestorProfile from "pages/fundcorp/_components/InvestorProfile";
import RescueTicket from "pages/fundcorp/_components/RescueTicket";
import FundSubscribeSummary from "pages/fundcorp/_components/_subscribe/FundSubscribeSummary";
import ShareAccountTicket from "pages/fundcorp/_components/_subscribe/ShareAccountTicket";
import SubscribeTicket from "pages/fundcorp/_components/_subscribe/SubscribeTicket";
import ListDirectDebit from "pages/v2/directDebit/ListDirectDebit";
import NewDirectDebit from "pages/v2/directDebit/NewDirectDebit";
import NewDirectDebitSummary from "pages/v2/directDebit/NewDirectDebitSummary";
import NewDirectDebitSummaryData from "pages/v2/directDebit/NewDirectDebitSummaryData";
import NewDirectDebitTransaction from "pages/v2/directDebit/NewDirectDebitTransaction";
import DetailDirectDebit from "pages/v2/directDebit/DetailDirectDebit";
import RequestDebin from "pages/charges/_components/RequestDebin";
import SentDebinDetail from "pages/charges/SentDebinDetail";
import CreateDebinSummary from "pages/charges/CreateDebinSummary";
import DebinRequests from "pages/charges/DebinRequests";
import DebinAddAccounts from "pages/charges/DebinAddAccounts";
import DebinAddAccountsSummary from "pages/charges/DebinAddAccountsSummary";
import DebinCancelSummary from "pages/charges/DebinCancelSummary";
import PrivacyPolicy from "pages/_components/PrivacyPolicy";
import SupportInfo from "pages/_components/SupportInfo";
import TermsAndConditions from "pages/_components/TermsAndConditions";
import Administration from "pages/administration/Administration";
import UserInvite from "pages/administration/UserInvite";
import UserInviteStep2 from "pages/administration/UserInviteStep2";
import UserInviteGroupSignature from "pages/administration/UserInviteGroupSignature";
import UserInviteStep3 from "pages/administration/UserInviteStep3";
import AdministrationMediumSignatureTicket from "pages/administration/medium/tickets/SignatureTicket";
import NewPaymentThirdParty from "pages/v2/payThirdParties/NewPaymentThirdParty";
import PaymentsMultipleArcaTransaction from "pages/v2/payments/PaymentsMultipleArcaTransaction";
import PayPaymentAFIP from "pages/servicePayments/PayPaymentAFIP";
import EcheqsMassManageBatchDetail from "pages/v2/echeqMassManagement/EcheqsMassManageBatchDetail";
import EcheqsMassManageNotificationEntry from "pages/v2/echeqMassManagement/EcheqsMassManageNotificationEntry";

export { matchesV3PrivateShellPath };

/**
 * @param {object|null|undefined} activeEnvironment
 * @param {boolean} uxDashboardImprovements
 * @returns {React.ReactElement[]}
 */
export default function renderV3PrivateShellRoutes(activeEnvironment, uxDashboardImprovements) {
    if (!uxDashboardImprovements) {
        return [];
    }

    const home = getPostLoginHomePath(uxDashboardImprovements);

    const shellRender = () => (
        <V3PrivateShellRoute authorizedRoute>
            <ConnectedV3PrivateShellSwitchInner activeEnvironment={activeEnvironment} home={home} />
        </V3PrivateShellRoute>
    );

    return V3_PRIVATE_SHELL_PATH_MATCHERS.map((matcher, index) => (
        <Route
            key={`v3-private-shell-${String(index)}-${matcher.path}`}
            path={matcher.path}
            exact={matcher.exact === true}
            render={shellRender}
        />
    ));
}

/**
 * @param {object} props
 * @param {object|null|undefined} props.activeEnvironment
 * @param {string} props.home
 * @param {boolean} props.featureSignatureDispatchControl
 * @param {boolean} props.isAdministrator
 */
function V3PrivateShellSwitchInner({ activeEnvironment, home, featureSignatureDispatchControl, isAdministrator }) {
    const perm = activeEnvironment?.permissions;
    const canFrequentDestination = !!perm?.frequentDestination;
    const canManage = !!perm?.["frequentDestination.manage"];
    const canHistoric = canAccessTransferHistoric(perm);
    const canV2TransferForms = canAccessV2TransferForms(perm);
    const canPendingAuthorizations = canAccessPendingAuthorizationsShell(
        activeEnvironment,
        featureSignatureDispatchControl,
    );
    const canPayDebin = !!perm?.["pay.debin"];
    const canPayThirdParties = !!perm?.["pay.third.parties"];
    const canPayAfip = !!perm?.["pay.afip"];
    const canServicePaymentsMain = !!(canPayAfip && !!perm?.["service.payments.view"]);
    const canTransferMultiple = !!perm?.transferMultiple;
    const canEcheqsControlShell = !!perm?.["operate.echeqs"];
    const canRequestCheckbookShell = !!perm?.requestCheckbook;
    const canChecksDetailShell = !!perm?.["checks.detail.view"];
    const canEcheqRulesAccessShell = !!perm?.["rule.access.echeq"];
    const canEcheqRuleManageShell = !!perm?.["rule.manage.echeq"];
    const canOperateEcheqs = !!perm?.["operate.echeqs"];
    const canMassiveEmitEcheq = !!perm?.massiveEmitEcheq;
    const canMassEcheqManage = canAccessMassEcheqManagementList(perm);
    const canCreateMassEcheqBatch = canCreateMassEcheqManagementBatch(perm);
    const canMetrocorp = !!perm?.metrocorp;
    const canCurrencyExchange =
        !!perm?.["investment.currencyExchange.buy"] || !!perm?.["investment.currencyExchange.sell"];
    const canFundcorpInformation = !!perm?.fundcorpInformation;
    const canFundcorpOperations = !!perm?.fundcorpOperations;
    const canFundRescue = !!perm?.fundRescue;
    const canFundSubscribe = !!perm?.fundSubscribe;
    const canCedip = !!perm?.cedip;
    const canDeposits = !!perm?.deposits;
    const canDepositsConstitute = !!perm?.depositsConstitute;
    const canFinancingList = !!perm?.financingList;
    const canDirectDebitMenu = !!perm?.["directDebit.access.menu"];
    const canDirectDebitManage = !!perm?.["directDebit.manage"];
    const canCharges = !!perm?.charges;
    const canAccountOpening = !!perm?.accountOpening;
    const isRetailEnvironment = activeEnvironment?.type === ENVIRONMENT_TYPE.RETAIL;
    const currentDisplay = getDisplay();
    const isDesktop = currentDisplay === "desktop" || currentDisplay === "smallDesktop";
    const ecomexShellIsMobile = () => {
        const display = getDisplay();
        return display === "mobile" || display === "tablet";
    };
    const ecomexNewRequestsAllowed = () => {
        const isMobile = ecomexShellIsMobile();
        const p = activeEnvironment?.permissions || {};
        const ecomexMobileEnabled = config.getBoolean("ecomex.access.from.app.enabled", false);
        const show = !ecomexMobileEnabled
            ? !isMobile && config.getBoolean("backend.show.ecomexTransaccional.newRequests")
            : config.getBoolean("backend.show.ecomexTransaccional.newRequests");
        return !!(show && p.ecomexNewRequests);
    };
    const ecomexRequestHistoryAllowed = () => {
        const isMobile = ecomexShellIsMobile();
        const p = activeEnvironment?.permissions || {};
        const ecomexMobileEnabled = config.getBoolean("ecomex.access.from.app.enabled", false);
        const show = !ecomexMobileEnabled
            ? !isMobile && config.getBoolean("backend.show.ecomexTransaccional.requestHistory")
            : config.getBoolean("backend.show.ecomexTransaccional.newRequests");
        return !!(show && p.ecomexRequestHistory);
    };
    const renderEcomexFlowShell = (Component) => (routeProps) => <Component {...routeProps} embeddedInV3Shell />;

    const renderPayDebin = (Component) => (routeProps) => {
        if (!canPayDebin) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderFixedTermAcceptanceShell = (Component) => (routeProps) => (
        <Component {...routeProps} embeddedInV3Shell />
    );

    const renderCurrencyExchangeShell = (Component) => (routeProps) => {
        if (!canCurrencyExchange) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderFundInformationShell = (Component) => (routeProps) => {
        if (!canFundcorpInformation) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderCedipShell = (Component) => (routeProps) => {
        if (!canCedip) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderFundcorpOperationsShell = (Component) => (routeProps) => {
        if (!canFundcorpOperations) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderFundRescueShell = (Component) => (routeProps) => {
        if (!canFundRescue) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderFundSubscribeShell = (Component) => (routeProps) => {
        if (!canFundSubscribe) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderDepositsShell = (Component) => (routeProps) => {
        if (!canDeposits) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderDepositsConstituteShell = (Component) => (routeProps) => {
        if (!canDepositsConstitute) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderFinancingShell = (Component) => (routeProps) => {
        if (!canFinancingList) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderDirectDebitMenuShell = (Component) => (routeProps) => {
        if (!canDirectDebitMenu) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderDirectDebitManageShell = (Component) => (routeProps) => {
        if (!canDirectDebitManage) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderChargesGenerateDebinShell = (Component) => (routeProps) => {
        if (!canCharges) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderChargesDebinFlowShell = (Component) => (routeProps) => <Component {...routeProps} embeddedInV3Shell />;

    const renderLegalInfoShell = (Component) => (routeProps) => <Component {...routeProps} embeddedInV3Shell />;

    const renderPayThirdParties = (Component, extraProps = {}) => (routeProps) => {
        if (!canPayThirdParties) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} {...extraProps} embeddedInV3Shell />;
    };

    const renderPayAfip = (Component, extraProps = {}) => (routeProps) => {
        if (!canPayAfip) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} {...extraProps} embeddedInV3Shell />;
    };

    const renderServicePaymentsMain = (Component) => (routeProps) => {
        if (!canServicePaymentsMain) {
            return <Redirect to={home} />;
        }
        return <Component {...routeProps} embeddedInV3Shell />;
    };

    const renderCommunicationsShell = (Component) => (routeProps) => (
        <Component {...routeProps} embeddedInV3Shell isDesktop={isDesktop} />
    );

    const frequentDefs = buildFrequentDestinationDefinitions(activeEnvironment);
    const corporateDefs = buildCorporateCardDefinitions(activeEnvironment);

    return (
        <Switch>
            <Route exact path="/v3/dashboard" render={(rp) => <DashboardV3 {...rp} />} />
            <Route exact path="/accounts/:id" render={(rp) => <AccountDetail {...rp} embeddedInV3Shell />} />
            <Route exact path="/v2/accounts" render={(rp) => <AccountsV2Page {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/v3/accounts"
                render={(rp) => {
                    if (isUxMyAccountsImprovementsEnabled()) {
                        return <AccountsV3Page {...rp} />;
                    }
                    return <Redirect to={getV3PrivateNavAccountsPath()} />;
                }}
            />
            <Route
                exact
                path="/v3/accounts/create"
                render={(rp) => {
                    if (!canAccountOpening) {
                        return <Redirect to={home} />;
                    }
                    return <AccountCreateV3Page {...rp} />;
                }}
            />
            <Route
                exact
                path="/v3/accounts/edit"
                render={(rp) => {
                    if (!canAccountOpening) {
                        return <Redirect to={home} />;
                    }
                    return <AccountCreateV3Page {...rp} />;
                }}
            />
            <Route
                exact
                path="/v3/accounts/:id/projectedBalance"
                render={(rp) => {
                    if (isUxMyAccountsImprovementsEnabled()) {
                        return <AccountProjectedBalanceV3Page {...rp} />;
                    }
                    return <Redirect to={`/account/proyectedBalance/${rp.match.params.id}`} />;
                }}
            />
            <Route
                exact
                path="/v3/accounts/:id/historicBalance"
                render={(rp) => {
                    if (isUxMyAccountsImprovementsEnabled()) {
                        return <AccountHistoricBalanceV3Page {...rp} />;
                    }
                    return <Redirect to={`/account/historicBalance/${rp.match.params.id}`} />;
                }}
            />
            <Route
                exact
                path="/v3/accounts/:id"
                render={(rp) => {
                    if (isUxMyAccountsImprovementsEnabled()) {
                        return <AccountDetailV3Page {...rp} />;
                    }
                    return <Redirect to={`/accounts/${rp.match.params.id}`} />;
                }}
            />
            <Route
                exact
                path="/v3/accountOpening/summary"
                render={(rp) => {
                    if (!canAccountOpening) {
                        return <Redirect to={home} />;
                    }
                    return <AccountOpeningSummaryV3Page {...rp} />;
                }}
            />
            <Route
                exact
                path="/v3/account/create/:status/:id"
                render={(rp) => {
                    if (!canAccountOpening) {
                        return <Redirect to={home} />;
                    }
                    return <AccountTicketV3Page {...rp} />;
                }}
            />
            <Route
                exact
                path="/accountOpening"
                render={(rp) => {
                    if (!canAccountOpening) {
                        return <Redirect to={home} />;
                    }
                    return <AccountOpening {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/accountOpening/back"
                render={(rp) => {
                    if (!canAccountOpening) {
                        return <Redirect to={home} />;
                    }
                    return <AccountOpening {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/accountOpening/modify"
                render={(rp) => {
                    if (!canAccountOpening) {
                        return <Redirect to={home} />;
                    }
                    return <AccountOpening {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/accountOpening/summary"
                render={(rp) => {
                    if (!canAccountOpening) {
                        return <Redirect to={home} />;
                    }
                    return <AccountOpeningSummary {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/accountOpening/transaction"
                render={(rp) => {
                    if (!canAccountOpening) {
                        return <Redirect to={home} />;
                    }
                    return <AccountOpeningTicket {...rp} embeddedInV3Shell />;
                }}
            />
            <Route exact path="/accountsOfficer" render={(rp) => <AccountsOfficer {...rp} embeddedInV3Shell />} />
            <Route exact path="/settings" render={(rp) => <Settings {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/settings/changePassword"
                render={(rp) => <ChangePassword {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/changeUserpass"
                render={(rp) => <ChangeUserpass {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/changeSecuritySeal"
                render={(rp) => <ChangeSecuritySeal {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/changeSecuritySeal/confirmation"
                render={(rp) => <ChangeSecuritySealConfirmation {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/notificationsConfiguration"
                render={(rp) => <NotificationsConfiguration {...rp} embeddedInV3Shell />}
            />
            <Route
                path="/settings/notificationsConfiguration/:communicationType"
                render={(rp) => <NotificationsConfigurationTransports {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/fingerprintConfiguration"
                render={(rp) => <FingerprintConfiguration {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/fingerprintConfiguration/deleteConfirmation"
                render={(rp) => <FingerprintDeleteConfirmation {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/notificationsPushDevices"
                render={(rp) => <ListDevices {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/settings/myDevices" render={(rp) => <MyDevices {...rp} embeddedInV3Shell />} />
            <Route exact path="/settings/token" render={(rp) => <Token {...rp} embeddedInV3Shell />} />
            <Route exact path="/settings/changeEmail" render={(rp) => <ChangeEmail {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/settings/changeEmail/confirmation"
                render={(rp) => <ChangeEmailConfirmation {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/ChangeAddresses"
                render={(rp) => <ChangeAddresses {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/settings/changePhone" render={(rp) => <ChangePhone {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/settings/changePhone/confirmation"
                render={(rp) => <ChangePhoneConfirmation {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/setDefaultEnvironment"
                render={(rp) => <SetDefaultEnvironment {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/changeEnvironment"
                render={(rp) => <ChangeEnvironment {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/changeLanguage"
                render={(rp) => <ChangeLanguage {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/settings/LastLogin" render={(rp) => <LastLogin {...rp} embeddedInV3Shell />} />
            <Route exact path="/settings/qAlert" render={(rp) => <QAlertConfiguration {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/settings/esign/documents"
                render={(rp) => <DocumentsIndex {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/esign/documentsSigned"
                render={(rp) => <EsignDocumentsSigned {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/pointsOfInterest"
                render={(rp) => <PointsOfInterest {...rp} embeddedInV3Shell />}
            />
            {/* Settings token sub-flows */}
            <Route
                exact
                path="/settings/token/codeActivation"
                render={(rp) => <CodeActivationMobile {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/codeActivation/changuedPassword"
                render={(rp) => <CodeActivationPasswordMobile {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/codeActivation/biometricActivation"
                render={(rp) => <ActivateTokenWithBiometrics {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/ChangeTokenPass"
                render={(rp) => <ChangeTokenPass {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/tokenPassword"
                render={(rp) => <TokenPassword {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/tokenRandomCode"
                render={(rp) => <TokenRandomCode {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/recoverPass"
                render={(rp) => <RecoverToken {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/disabledTokenPass"
                render={(rp) => <DisabledTokenPass {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/settings/token/request" render={(rp) => <TokenDrawer {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/settings/token/disableBiometricToken"
                render={(rp) => <DisableBiometricToken {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/synchronization/ValidateRandomCode"
                render={(rp) => <TokenValidateRandomCode {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/synchronization/tokenPassword"
                render={(rp) => <TokenPassword {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/synchronization/biometricActivation"
                render={(rp) => <ActivateTokenWithBiometrics {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/settings/token/tokenSeedError"
                render={(rp) => <TokenSeedError {...rp} embeddedInV3Shell />}
            />
            {/* Administration — rutas específicas antes del catch-all */}
            <Route
                exact
                path="/administration/users/invite"
                render={(rp) => {
                    if (!isAdministrator) {
                        return <Redirect to={home} />;
                    }
                    return <UserInvite {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/administration/users/inviteStep2"
                render={(rp) => {
                    if (!isAdministrator) {
                        return <Redirect to={home} />;
                    }
                    return <UserInviteStep2 {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/administration/users/GroupSignature"
                render={(rp) => {
                    if (!isAdministrator) {
                        return <Redirect to={home} />;
                    }
                    return <UserInviteGroupSignature {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/administration/users/inviteStep3"
                render={(rp) => {
                    if (!isAdministrator) {
                        return <Redirect to={home} />;
                    }
                    return <UserInviteStep3 {...rp} embeddedInV3Shell isDesktop={isDesktop} />;
                }}
            />
            <Route
                exact
                path="/administration/medium/signature/:idTransaction/ticket"
                render={(rp) => {
                    if (!isAdministrator) {
                        return <Redirect to={home} />;
                    }
                    return <AdministrationMediumSignatureTicket {...rp} embeddedInV3Shell isDesktop={isDesktop} />;
                }}
            />
            {/* Administration catch-all: Administration maneja el Switch interno */}
            <Route
                path="/administration"
                render={(rp) => {
                    if (!isAdministrator) {
                        return <Redirect to={home} />;
                    }
                    return <Administration {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/account/proyectedBalance/:id"
                render={(rp) => <ProyectedBalance {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/account/historicBalance/:id"
                render={(rp) => <HistoricBalance {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/account/:id/setProductAlias"
                render={(rp) => <SetProductAlias {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/ConsolidatedPosition"
                render={(rp) => <ConsolidatedPosition {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/v2/agenda/create"
                render={(rp) => {
                    if (!canManage) {
                        return <Redirect to={home} />;
                    }
                    return <CreateContactPage {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/agenda"
                render={(rp) => {
                    if (!canFrequentDestination) {
                        return <Redirect to={home} />;
                    }
                    return <Agenda {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v3/transfer/historic"
                render={(rp) => {
                    if (!canHistoric) {
                        return <Redirect to={home} />;
                    }
                    if (!isTransferHistoricV3Enabled()) {
                        return <Redirect to="/v2/transfer/historic" />;
                    }
                    return <TransferHistoric {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/transfer/historic"
                render={(rp) => {
                    if (!canHistoric) {
                        return <Redirect to={home} />;
                    }
                    if (isTransferHistoricV3Enabled()) {
                        return <Redirect to={TRANSFER_HISTORIC_V3_PATH} />;
                    }
                    return <TransferHistoric {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v3/transfer/download"
                render={(rp) => {
                    if (!canHistoric) {
                        return <Redirect to={home} />;
                    }
                    if (!isTransferHistoricV3Enabled()) {
                        return <Redirect to="/v2/transferDownload" />;
                    }
                    return <TransferDownloadV3Page {...rp} />;
                }}
            />
            <Route
                exact
                path="/v2/transferDownload"
                render={(rp) => {
                    if (!canHistoric) {
                        return <Redirect to={home} />;
                    }
                    if (isTransferHistoricV3Enabled()) {
                        return <Redirect to={TRANSFER_DOWNLOAD_V3_PATH} />;
                    }
                    return <TransferDownload {...rp} embeddedInV3Shell />;
                }}
            />
            {/* Deben ir antes de `/v3/transfer/:transferType`, que si no captura `confirmation` y `ticket`. */}
            <Route
                exact
                path="/v3/transfer/confirmation"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    if (!isTransferHistoricV3Enabled()) {
                        return <Redirect to={TRANSFER_CONFIRMATION_LEGACY_PATH} />;
                    }
                    return <TransferConfirmationV3Page {...rp} />;
                }}
            />
            <Route
                exact
                path="/v3/transfer/ticket"
                render={(rp) => {
                    if (!canV2TransferForms && !canHistoric) {
                        return <Redirect to={home} />;
                    }
                    if (!isTransferHistoricV3Enabled()) {
                        return <Redirect to={getTransferTicketPath()} />;
                    }
                    return <TransferTicketV3Page {...rp} />;
                }}
            />
            <Route
                exact
                path="/v3/transfer/:transferType/modify"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    if (!isTransferHistoricV3Enabled()) {
                        return (
                            <Redirect to={getTransferExecutePath(rp.match.params.transferType, { suffix: "modify" })} />
                        );
                    }
                    return <ExecuteTransferV3Page key={rp.location.pathname} {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v3/transfer/:transferType/back"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    if (!isTransferHistoricV3Enabled()) {
                        return (
                            <Redirect to={getTransferExecutePath(rp.match.params.transferType, { suffix: "back" })} />
                        );
                    }
                    return <ExecuteTransferV3Page key={rp.location.pathname} {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v3/transfer/:transferType"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    if (!isTransferHistoricV3Enabled()) {
                        return <Redirect to={getTransferExecutePath(rp.match.params.transferType)} />;
                    }
                    return <ExecuteTransferV3Page key={rp.location.pathname} {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/transfer/:transferType/modify"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    if (isTransferHistoricV3Enabled()) {
                        return (
                            <Redirect to={getTransferExecutePath(rp.match.params.transferType, { suffix: "modify" })} />
                        );
                    }
                    return <V2Transfer key={rp.location.pathname} {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/transfer/:transferType/back"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    if (isTransferHistoricV3Enabled()) {
                        return (
                            <Redirect to={getTransferExecutePath(rp.match.params.transferType, { suffix: "back" })} />
                        );
                    }
                    return <V2Transfer key={rp.location.pathname} {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/transfer/:transferType"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    if (isTransferHistoricV3Enabled()) {
                        return <Redirect to={getTransferExecutePath(rp.match.params.transferType)} />;
                    }
                    return <V2Transfer key={rp.location.pathname} {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/multiple"
                render={(rp) => {
                    if (!canTransferMultiple) {
                        return <Redirect to={home} />;
                    }
                    return <MultipleTransferDashboard {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/multiple/create"
                render={(rp) => {
                    if (!canTransferMultiple) {
                        return <Redirect to={home} />;
                    }
                    return <MultipleTransfer {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/multiple/confirm"
                render={(rp) => {
                    if (!canTransferMultiple) {
                        return <Redirect to={home} />;
                    }
                    return <MultipleTransferSummary {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/multiple/detail/:id"
                render={(rp) => {
                    if (!canTransferMultiple) {
                        return <Redirect to={home} />;
                    }
                    return <MultipleTransferDetail {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/multiple/cancelled"
                render={(rp) => {
                    if (!canTransferMultiple) {
                        return <Redirect to={home} />;
                    }
                    return <MultipleTransferTicket {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/multiple/pending"
                render={(rp) => {
                    if (!canTransferMultiple) {
                        return <Redirect to={home} />;
                    }
                    return <MultipleTransferTicket {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/multiple/failed"
                render={(rp) => {
                    if (!canTransferMultiple) {
                        return <Redirect to={home} />;
                    }
                    return <MultipleTransferTicket {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/multiple/confirmation"
                render={(rp) => {
                    if (!canTransferMultiple) {
                        return <Redirect to={home} />;
                    }
                    return <MultipleTransferTicket {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/echeqs/control"
                render={(rp) => {
                    if (!canEcheqsControlShell) {
                        return <Redirect to={home} />;
                    }
                    return <EcheqsControl {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                path="/echeqs/mass-management/:envId/:envName"
                render={(rp) => {
                    if (!canMassEcheqManage) {
                        return <Redirect to={home} />;
                    }
                    return <EcheqsMassManageNotificationEntry {...rp} />;
                }}
            />
            <Route
                exact
                path="/v2/echeqs/mass-management"
                render={(rp) => {
                    if (!canMassEcheqManage) {
                        return <Redirect to={home} />;
                    }
                    return <EcheqsMassManageList {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/echeqs/mass-management/new"
                render={(rp) => {
                    if (!canCreateMassEcheqBatch) {
                        return <Redirect to={canMassEcheqManage ? "/v2/echeqs/mass-management" : home} />;
                    }
                    return <EcheqsMassManageForm {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/echeqs/mass-management/confirm"
                render={(rp) => {
                    if (!canCreateMassEcheqBatch) {
                        return <Redirect to={canMassEcheqManage ? "/v2/echeqs/mass-management" : home} />;
                    }
                    return <EcheqsMassManageConfirm {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/echeqs/mass-management/ticket"
                render={(rp) => {
                    if (!canCreateMassEcheqBatch) {
                        return <Redirect to={canMassEcheqManage ? "/v2/echeqs/mass-management" : home} />;
                    }
                    return <EcheqsMassManageTicket {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/echeqs/mass-management/:idBatch/detail"
                render={(rp) => {
                    if (!canMassEcheqManage) {
                        return <Redirect to={home} />;
                    }
                    return <EcheqsMassManageBatchDetail {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/checks"
                render={(rp) => {
                    if (!canRequestCheckbookShell) {
                        return <Redirect to={home} />;
                    }
                    return <Checks {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/checks/summary"
                render={(rp) => {
                    if (!canRequestCheckbookShell) {
                        return <Redirect to={home} />;
                    }
                    return <ChecksSummary {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/checks/back"
                render={(rp) => {
                    if (!canRequestCheckbookShell) {
                        return <Redirect to={home} />;
                    }
                    return <Checks {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/checks/modify"
                render={(rp) => {
                    if (!canRequestCheckbookShell) {
                        return <Redirect to={home} />;
                    }
                    return <Checks {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/checks/transaction"
                render={(rp) => {
                    if (!canRequestCheckbookShell) {
                        return <Redirect to={home} />;
                    }
                    return <CheckTransaction {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/checksDetail/:checkNumber"
                render={(rp) => {
                    if (!canChecksDetailShell) {
                        return <Redirect to={home} />;
                    }
                    return <ChecksDetailInfo {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/checksDetail"
                render={(rp) => {
                    if (!canChecksDetailShell) {
                        return <Redirect to={home} />;
                    }
                    return <ChecksDetail {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/discountedChecksDetail/:checkNumber"
                render={(rp) => {
                    if (!canChecksDetailShell) {
                        return <Redirect to={home} />;
                    }
                    return <DiscountedChecksDetailInfo {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/discountedChecksDetail"
                render={(rp) => {
                    if (!canChecksDetailShell) {
                        return <Redirect to={home} />;
                    }
                    return <DiscountedChecksDetail {...rp} embeddedInV3Shell />;
                }}
            />
            {buildV3EcheqsShellRoutes({ home, canOperateEcheqs, canMassiveEmitEcheq })}
            <Route
                exact
                path="/v2/echeqRules/create/confirmation"
                render={(rp) => {
                    if (!canEcheqRulesAccessShell) {
                        return <Redirect to={home} />;
                    }
                    return <CreateEcheqRuleConfirmation {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/echeqRules/create"
                render={(rp) => {
                    if (!canEcheqRulesAccessShell) {
                        return <Redirect to={home} />;
                    }
                    return <CreateEcheqRule {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/echeqRules"
                render={(rp) => {
                    if (!canEcheqRulesAccessShell) {
                        return <Redirect to={home} />;
                    }
                    return <EcheqRulesMain {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/echeqRules/create/transaction"
                render={(rp) => {
                    if (!canEcheqRulesAccessShell) {
                        return <Redirect to={home} />;
                    }
                    return <CreateEcheqRuleTransaction {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/echeqRules/confirm/:action"
                render={(rp) => {
                    if (!canEcheqRuleManageShell) {
                        return <Redirect to={home} />;
                    }
                    return <EcheqRuleSummary {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/confirmation"
                render={(rp) => <TransferTransaction {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/transfer/cancelled"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    return <TransferTransaction {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/summary"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    return <TransferSummary {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/failed"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    return <TransferTransaction {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/scheduled"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    return <TransferScheduled {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/scheduled/cancel"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    return <TransferScheduledCancel {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/pending"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    return <TransferTransaction {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transfer/error"
                render={(rp) => {
                    if (!canV2TransferForms) {
                        return <Redirect to={home} />;
                    }
                    return <TransferError {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/transaction/:idTransaction/processDetail"
                render={(rp) => <TransactionPaymentLines {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/transaction/:idTransaction"
                render={(rp) => <Transaction {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/transactions/list" render={(rp) => <TransactionsList {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/transactions/list/filters"
                render={(rp) => <TransactionsListFilters {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/transactions/list/filters/results"
                render={(rp) => <TransactionsFiltersResults {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/transactions/cancel"
                render={(rp) => <TransactionTicketCancelConfirmation {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/communications" render={renderCommunicationsShell(CommunicationMain)} />
            <Route exact path="/communications/compose" render={renderCommunicationsShell(NewMessage)} />
            <Route exact path="/communications/reply/:id" render={renderCommunicationsShell(ReplyMessage)} />
            <Route exact path="/communications/read/:id" render={renderCommunicationsShell(ReadMessage)} />
            <Route exact path="/v2/payments-services" render={renderServicePaymentsMain(PaymentsServicesArca)} />
            <Route exact path="/v2/payAFIPSummary" render={renderPayAfip(PaymentServicesArcaConfirmationPage)} />
            <Route exact path="/servicePayments" render={renderServicePaymentsMain(ServicePaymentsMain)} />
            <Route
                exact
                path="/servicePaymentsConfirmationTicket"
                render={renderPayAfip(ServicePaymentsConfirmationTicket)}
            />
            <Route
                exact
                path="/agendaServicePaymentsConfirmationTicket"
                render={(rp) => {
                    if (!canServicePaymentsMain) {
                        return <Redirect to={home} />;
                    }
                    return <AgendaServicePaymentsConfirmationTicket {...rp} embeddedInV3Shell isDesktop={isDesktop} />;
                }}
            />
            <Route exact path="/newPayment" render={renderPayAfip(ServiceNewPayment)} />
            <Route exact path="/newPayment/pending" render={renderPayAfip(ServicePaymentsNewPaymentTicket)} />
            <Route exact path="/newPayment/cancelled" render={renderPayAfip(ServicePaymentsNewPaymentTicket)} />
            <Route exact path="/newPayment/confirmation" render={renderPayAfip(ServicePaymentsNewPaymentTicket)} />
            <Route exact path="/adhesiones/list" render={renderPayAfip(AdhesionesList)} />
            <Route
                exact
                path="/agenda/payment"
                render={(rp) => {
                    if (!canServicePaymentsMain) {
                        return <Redirect to={home} />;
                    }
                    return <AgendaPayment {...rp} embeddedInV3Shell isDesktop={isDesktop} />;
                }}
            />
            <Route
                exact
                path="/agenda/multiplePayment/detail/:key"
                render={(rp) => {
                    if (!canServicePaymentsMain) {
                        return <Redirect to={home} />;
                    }
                    return <MultiplePaymentAgendaDetail {...rp} embeddedInV3Shell isDesktop={isDesktop} />;
                }}
            />
            <Route exact path="/payMultipleAFIPSummary" render={renderPayAfip(MultipleAFIPSummary)} />
            <Route exact path="/deletePaymentAFIPSummary" render={renderPayAfip(DeletePaymentAFIPSummary)} />
            <Route exact path="/payPaymentAFIP/:idTransaction/ticket" render={renderPayAfip(PayPaymentAFIPTicket)} />
            <Route exact path="/payPaymentAFIP" render={renderPayAfip(PayPaymentAFIP)} />
            <Route exact path="/payPaymentAFIPConfirmation" render={renderPayAfip(PayPaymentAFIPConfirmation)} />
            <Route exact path="/payPaymentAFIPSummary" render={renderPayAfip(PayPaymentAFIPSummary, { isDesktop })} />
            <Route exact path="/payMultipleAFIPDetail" render={renderPayAfip(MultipleAFIPDetail)} />
            <Route exact path="/payMultipleAFIPSuccess" render={renderPayAfip(MultipleAFIPSuccess)} />
            <Route exact path="/v2/payMultipleAFIP/success" render={renderPayAfip(PaymentsMultipleArcaTransaction)} />
            <Route
                exact
                path="/multiplePaymentsConfirmation"
                render={renderServicePaymentsMain(MultiplePaymentsConfirmation)}
            />
            <Route exact path="/multiplePaymentsSuccess" render={renderServicePaymentsMain(MultiplePaymentsSuccess)} />
            <Route
                exact
                path="/deletePaymentAFIP/:idTransaction/ticket"
                render={renderPayAfip(DeletePaymentAFIPTicket)}
            />
            <Route exact path="/desadherir/confirmation" render={renderPayAfip(UnattachServiceConfirmationTicket)} />
            <Route exact path="/transactions/sign" render={(rp) => <TransactionTicketSignConfirmation {...rp} />} />
            <Route exact path="/externaltransactions/pending" render={(rp) => <ExternalTransaction {...rp} />} />
            <Route exact path="/externaltransactions/cancelled" render={(rp) => <ExternalTransaction {...rp} />} />
            <Route exact path="/externaltransactions/confirmation" render={(rp) => <ExternalTransaction {...rp} />} />
            <Route exact path="/externaltransactions/failed" render={(rp) => <ExternalTransaction {...rp} />} />
            <Route
                exact
                path="/v2/settings/hidebalance"
                render={(rp) => <HideBalancePage {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/payments/enabledContributors"
                render={(rp) => <EnabledContributorsList {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/v3/credin/ticket" render={(rp) => <CredinTicketV3Page {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/confirmCredin/transaction"
                render={(rp) => {
                    if (isCredinV3Enabled()) {
                        return <Redirect to="/v3/credin/ticket" />;
                    }
                    return <CredinPrepareConfirmation {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v3/credin/confirmation"
                render={(rp) => <GenerateCredinV3Page {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/v3/requestCredin/generateCredin"
                render={() => <Redirect to="/v3/credin/confirmation" />}
            />
            <Route
                exact
                path="/requestCredin/generateCredin"
                render={(rp) => {
                    if (isCredinV3Enabled()) {
                        return <Redirect to="/v3/credin/confirmation" />;
                    }
                    return <GenerateCredin {...rp} embeddedInV3Shell />;
                }}
            />
            <Route exact path="/v3/credin" render={(rp) => <CreateCredinV3Page {...rp} embeddedInV3Shell />} />
            <Route exact path="/v3/requestCredin" render={() => <Redirect to="/v3/credin" />} />
            <Route
                exact
                path="/requestCredin"
                render={(rp) => {
                    if (isCredinV3Enabled()) {
                        return <Redirect to="/v3/credin" />;
                    }
                    return <RequestCredin {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/payments/payDebin/chargebackPull/confirm"
                render={renderPayDebin(ChargebackPullConfirm)}
            />
            <Route exact path="/payments/payDebin/chargeback/confirm" render={renderPayDebin(ChargebackDebinConfirm)} />
            <Route exact path="/payments/payDebin/decline/confirm" render={renderPayDebin(DeclineDebinConfirm)} />
            <Route exact path="/payments/payDebin/accept/confirm" render={renderPayDebin(AcceptDebinConfirm)} />
            <Route exact path="/payments/payDebin/detail/:idDebin" render={renderPayDebin(IncomingDebinDetail)} />
            <Route exact path="/payments/payDebin" render={renderPayDebin(IncomingDebinList)} />
            <Route exact path="/confirmIncomingDebin/transaction" render={renderPayDebin(IncomingDebinTransaction)} />
            <Route
                exact
                path="/fixedTermAcceptance/decline/confirm"
                render={renderFixedTermAcceptanceShell(DeclineFixedTermConfirm)}
            />
            <Route
                exact
                path="/fixedTermAcceptance/accept/confirm"
                render={renderFixedTermAcceptanceShell(AcceptFixedTermConfirm)}
            />
            <Route
                exact
                path="/fixedTermAcceptance/detail/:idDebin"
                render={renderFixedTermAcceptanceShell(FixedTermAcceptanceDetail)}
            />
            <Route exact path="/fixedTermAcceptance" render={renderFixedTermAcceptanceShell(FixedTermAcceptance)} />
            <Route
                exact
                path="/v3/metrocorp"
                render={(rp) => {
                    if (!canMetrocorp) {
                        return <Redirect to={home} />;
                    }
                    if (!isUxMetrocorpImprovementsEnabled()) {
                        return <Redirect to="/metrocorp" />;
                    }
                    return <MetrocorpOperationsV3Page {...rp} />;
                }}
            />
            <Route
                exact
                path="/metrocorp"
                render={(rp) => {
                    if (!canMetrocorp) {
                        return <Redirect to={home} />;
                    }
                    if (isUxMetrocorpImprovementsEnabled()) {
                        return <Redirect to="/v3/metrocorp" />;
                    }
                    return <MetrocorpMain {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/v2/investment/currencyExchange"
                render={renderCurrencyExchangeShell(CurrencyExchangePage)}
            />
            <Route
                exact
                path="/v2/investment/currencyExchange/confirm"
                render={renderCurrencyExchangeShell(CurrencyExchangeConfirmPage)}
            />
            <Route
                exact
                path="/currencyExchangeConfirmation/:idTransaction/ticket"
                render={renderCurrencyExchangeShell(CurrencyExchangeTicket)}
            />
            <Route exact path="/fundInformation" render={renderFundInformationShell(FundcorpInformationMain)} />
            <Route exact path="/fundcorpOperations" render={renderFundcorpOperationsShell(FundcorpOperationsMain)} />
            <Route exact path="/fundcorpOperations/rescue" render={renderFundRescueShell(FundRescue)} />
            <Route
                exact
                path="/fundcorpOperations/rescue/:fondoNumero/:fondoAbreviatura/:fondoMoneda/:cuotapartistaNumero"
                render={renderFundRescueShell(FundRescue)}
            />
            <Route exact path="/fundcorpOperations/rescue/back" render={renderFundRescueShell(FundRescue)} />
            <Route exact path="/fundcorpOperations/rescue/pending" render={renderFundRescueShell(RescueTicket)} />
            <Route exact path="/fundcorpOperations/rescue/confirmation" render={renderFundRescueShell(RescueTicket)} />
            <Route exact path="/fundcorpOperations/rescue/cancelled" render={renderFundRescueShell(RescueTicket)} />
            <Route exact path="/fundcorpOperations/rescue/failed" render={renderFundRescueShell(RescueTicket)} />
            <Route exact path="/fundcorpOperations/rescue/scheduled" render={renderFundRescueShell(RescueTicket)} />
            <Route exact path="/rescue/scheduled/cancel" render={renderFundRescueShell(FundScheduledCancel)} />
            <Route exact path="/rescue/summary" render={renderFundRescueShell(FundRescueSummary)} />
            <Route exact path="/cancelSubscribe/summary" render={renderFundRescueShell(FundCancelSummary)} />
            <Route
                exact
                path="/cancelSubscribe/transaction"
                render={(routeProps) =>
                    renderFundRescueShell(CancelTicket)({
                        ...routeProps,
                        isDesktop,
                        isSmallDesktop: currentDisplay === "smallDesktop",
                    })
                }
            />
            <Route exact path="/fundcorpOperations/subscribe" render={renderFundSubscribeShell(FundSubscribe)} />
            <Route
                exact
                path="/fundcorpOperations/subscribe/:fondoNumero/:cuotapartistaNumero"
                render={renderFundSubscribeShell(FundSubscribe)}
            />
            <Route exact path="/subscribe/summary" render={renderFundSubscribeShell(FundSubscribeSummary)} />
            <Route exact path="/subscribe/pending" render={renderFundSubscribeShell(SubscribeTicket)} />
            <Route exact path="/subscribe/confirmation" render={renderFundSubscribeShell(SubscribeTicket)} />
            <Route exact path="/subscribe/cancelled" render={renderFundSubscribeShell(SubscribeTicket)} />
            <Route exact path="/subscribe/failed" render={renderFundSubscribeShell(SubscribeTicket)} />
            <Route exact path="/subscribe/scheduled" render={renderFundRescueShell(SubscribeTicket)} />
            <Route exact path="/subscribe/scheduled/cancel" render={renderFundSubscribeShell(FundScheduledCancel)} />
            <Route
                exact
                path="/fundcorpOperations/investor-profile"
                render={renderFundSubscribeShell(InvestorProfile)}
            />
            <Route
                exact
                path="/fundcorpOperations/registerShareAccount"
                render={renderFundSubscribeShell(FundRegisterShareAccount)}
            />
            <Route exact path="/registerShareAccount/success" render={renderFundSubscribeShell(ShareAccountTicket)} />
            <Route exact path="/registerShareAccount/failed" render={renderFundSubscribeShell(ShareAccountTicket)} />
            <Route exact path="/cedip" render={renderCedipShell(CedipList)} />
            <Route exact path="/cedip/:idCedip/:fraction" render={renderCedipShell(CedipDetail)} />
            <Route exact path="/cedip/reject" render={renderCedipShell(AcceptOrRejectCedip)} />
            <Route exact path="/cedip/accept" render={renderCedipShell(AcceptOrRejectCedip)} />
            <Route exact path="/cedip/ticket" render={renderCedipShell(RejectCedipTicket)} />
            <Route exact path="/v2/directDebit/list" render={renderDirectDebitMenuShell(ListDirectDebit)} />
            <Route exact path="/v2/directDebit/create" render={renderDirectDebitManageShell(NewDirectDebit)} />
            <Route exact path="/directDebit/summary" render={renderDirectDebitManageShell(NewDirectDebitSummary)} />
            <Route
                exact
                path="/directDebit/summaryData"
                render={renderDirectDebitManageShell(NewDirectDebitSummaryData)}
            />
            <Route
                exact
                path="/directDebit/transaction"
                render={renderDirectDebitMenuShell(NewDirectDebitTransaction)}
            />
            <Route
                exact
                path="/directDebit/details/:idTransaction"
                render={renderDirectDebitMenuShell(DetailDirectDebit)}
            />
            <Route exact path="/charges/generateDebin" render={renderChargesGenerateDebinShell(DebinRequests)} />
            <Route exact path="/charges/requestDebin" render={renderChargesDebinFlowShell(RequestDebin)} />
            <Route exact path="/charges/debin/detail/:idDebin" render={renderChargesDebinFlowShell(SentDebinDetail)} />
            <Route
                exact
                path="/charges/requestDebin/summary"
                render={renderChargesDebinFlowShell(CreateDebinSummary)}
            />
            <Route exact path="/charges/debin/addAccounts" render={renderChargesDebinFlowShell(DebinAddAccounts)} />
            <Route
                exact
                path="/charges/debin/addAccounts/summary"
                render={renderChargesDebinFlowShell(DebinAddAccountsSummary)}
            />
            <Route
                exact
                path="/charges/debin/cancel/summary"
                render={renderChargesDebinFlowShell(DebinCancelSummary)}
            />
            <Route exact path="/privacyPolicy" render={renderLegalInfoShell(PrivacyPolicy)} />
            <Route exact path="/support" render={renderLegalInfoShell(SupportInfo)} />
            <Route exact path="/termsAndConditions" render={renderLegalInfoShell(TermsAndConditions)} />
            <Route exact path="/deposit/:id/setProductAlias" render={renderDepositsShell(SetProductAlias)} />
            <Route
                exact
                path="/depositsConfirmation/:idTransaction/ticket"
                render={renderDepositsConstituteShell(DepositConstituteTicket)}
            />
            <Route exact path="/depositsSummary" render={renderDepositsConstituteShell(DepositSummary)} />
            <Route exact path="/v2/depositsConstitute" render={renderDepositsConstituteShell(DepositsConstituteV2)} />
            <Route exact path="/depositsConstitute" render={renderDepositsConstituteShell(DepositConstitute)} />
            <Route exact path="/deposits/:id" render={renderDepositsShell(DepositDetails)} />
            <Route exact path="/deposits" render={renderDepositsShell(Deposits)} />
            <Route
                exact
                path="/financing"
                render={() => (canFinancingList ? <Redirect to="/financing/categories" /> : <Redirect to={home} />)}
            />
            <Route exact path="/financing/categories" render={renderFinancingShell(FinancingListCategories)} />
            <Route exact path="/financing/feeds/detail/:id" render={renderFinancingShell(FeedsDetail)} />
            <Route exact path="/financing/detail/:id" render={renderFinancingShell(FinancingDetail)} />
            <Route exact path="/financing/:id" render={renderFinancingShell(FinancingId)} />
            <Route
                exact
                path="/ecomex"
                render={() => {
                    const landing = getEcomexPrimaryPath(activeEnvironment, ecomexShellIsMobile(), isRetailEnvironment);
                    return landing ? <Redirect to={landing} /> : <Redirect to={home} />;
                }}
            />
            <Route
                exact
                path="/ecomex/consultOperations"
                render={(rp) => {
                    if (!perm?.ecomexConsultOperations) {
                        return <Redirect to={home} />;
                    }
                    return <EcomexOperationsMain {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/ecomex/informativeRegime"
                render={(rp) => {
                    if (!perm?.ecomexInformativeRegime || isRetailEnvironment) {
                        return <Redirect to={home} />;
                    }
                    return <EcomexInformativeRegimeMain {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/ecomex/alertsAndDues"
                render={(rp) => {
                    if (!perm?.ecomexAlertsAndDues || isRetailEnvironment) {
                        return <Redirect to={home} />;
                    }
                    return <EcomexAlertsAndDuesMain {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/ecomex/newRequests"
                render={(rp) => {
                    if (!ecomexNewRequestsAllowed()) {
                        return <Redirect to={home} />;
                    }
                    return <EcomexNewRequests {...rp} embeddedInV3Shell />;
                }}
            />
            <Route
                exact
                path="/ecomex/requestHistory"
                render={(rp) => {
                    if (!ecomexRequestHistoryAllowed()) {
                        return <Redirect to={home} />;
                    }
                    return <EcomexRequestHistory {...rp} embeddedInV3Shell />;
                }}
            />
            <Route exact path="/ecomex/transaction" render={renderEcomexFlowShell(EcomexConfirmTransaction)} />
            <Route exact path="/ecomex/finished" render={renderEcomexFlowShell(ExcomexTransactionSuccess)} />
            <Route exact path="/ecomex/failed" render={renderEcomexFlowShell(EcomexTransaction)} />
            <Route exact path="/ecomex/pending" render={renderEcomexFlowShell(EcomexTransaction)} />
            <Route exact path="/ecomex/cancelled" render={renderEcomexFlowShell(EcomexTransaction)} />
            <Route
                exact
                path="/payments/recurrency/action/confirmation"
                render={(rp) => <ConfirmRecurrencyDebinAction {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/payments/recurrent/action/transaction"
                render={(rp) => <RecurrencyTransaction {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/payments/recurrent/load/transaction"
                render={(rp) => <PrestacionTransaction {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/payments/recurrent/load/confirm"
                render={(rp) => <ConfirmRecurrencyDebin {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/payments/recurrent/detail/:recurrencyId"
                render={(rp) => <RecurrencyDetail {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/payments/recurrent/load"
                render={(rp) => <LoadDebinRecurrency {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/payments/recurrent" render={(rp) => <RecurrentDebins {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/v2/debin/scheduled/confirmation"
                render={(rp) => <DebinScheduledConfirmationPage {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/v2/debin/scheduled/detail/:id"
                render={(rp) => <DebinScheduledDetail {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/v2/debin/scheduled" render={(rp) => <ProgrammedDebin {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/addlimitrequestconfirmation"
                render={(rp) => <AddLimitRequestConfirmation {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/addlimitrequest/cancelled"
                render={(rp) => <AddLimitTicket {...rp} embeddedInV3Shell />}
            />
            <Route
                exact
                path="/addlimitrequest/confirmation"
                render={(rp) => <AddLimitTicket {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/addlimitrequest/failed" render={(rp) => <AddLimitTicket {...rp} embeddedInV3Shell />} />
            <Route
                exact
                path="/addlimitrequest/pending"
                render={(rp) => <AddLimitTicket {...rp} embeddedInV3Shell />}
            />
            <Route exact path="/v2/addlimitrequest" render={(rp) => <AddLimitRequest {...rp} embeddedInV3Shell />} />
            <Route exact path="/payThirdParties/list" render={renderPayThirdParties(PayThirdPartiesList)} />
            <Route exact path="/payThirdParties/newPayment" render={renderPayThirdParties(NewPayment)} />
            <Route exact path="/v2/payThirdParties/newPayment" render={renderPayThirdParties(NewPaymentThirdParty)} />
            <Route exact path="/payThirdParties/summary" render={renderPayThirdParties(NewPaymentSummary)} />
            <Route exact path="/payThirdParties/confirmation" render={renderPayThirdParties(ThirdPaymentTicket)} />
            <Route exact path="/payThirdParties/pending" render={renderPayThirdParties(ThirdPaymentTicket)} />
            <Route exact path="/payThirdParties/cancelled" render={renderPayThirdParties(ThirdPaymentTicket)} />
            <Route
                exact
                path="/payThirdParties/:idTransaction/detail"
                render={renderPayThirdParties(ThirdPaymentDetail, {
                    isConfirmation: false,
                    shouldLoadForm: false,
                })}
            />
            {corporateDefs.map(({ path, authorizedRoute, component: Cmp, componentProps }) => (
                <Route
                    key={path}
                    exact
                    path={path}
                    render={(rp) => {
                        if (!authorizedRoute) {
                            return <Redirect to={home} />;
                        }
                        return <Cmp {...rp} {...(componentProps || {})} />;
                    }}
                />
            ))}
            {frequentDefs.map(({ path, authorizedRoute, component: Cmp, componentProps }) => (
                <Route
                    key={path}
                    exact
                    path={path}
                    render={(rp) => {
                        if (!authorizedRoute) {
                            return <Redirect to={home} />;
                        }
                        return <Cmp {...rp} {...(componentProps || {})} />;
                    }}
                />
            ))}
            {canPendingAuthorizations && (
                <>
                    <Route
                        exact
                        path="/pendingTransaction/list"
                        render={(rp) => <PendingTransactionList {...rp} embeddedInV3Shell />}
                    />
                    <Route
                        exact
                        path="/pendingTransaction/signMultiple"
                        render={(rp) => <MultipleSignaturesSummary {...rp} embeddedInV3Shell />}
                    />
                    <Route
                        exact
                        path="/pendingTransaction/deleteMultiple"
                        render={(rp) => <MultipleSignaturesDelete {...rp} embeddedInV3Shell />}
                    />
                    <Route
                        exact
                        path="/pendingTransaction/multipleSignatures/processing"
                        render={(rp) => <ProcessingMultipleSignatures {...rp} embeddedInV3Shell />}
                    />
                    <Route
                        exact
                        path="/pendingTransaction/multipleDelete/processing"
                        render={(rp) => <ProcessingMultipleDelete {...rp} embeddedInV3Shell />}
                    />
                </>
            )}
            <Route render={() => <Redirect to={home} />} />
        </Switch>
    );
}

V3PrivateShellSwitchInner.propTypes = {
    activeEnvironment: shape({}),
    home: string.isRequired,
    featureSignatureDispatchControl: bool.isRequired,
    isAdministrator: bool,
};

V3PrivateShellSwitchInner.defaultProps = {
    activeEnvironment: null,
    isAdministrator: false,
};

// `withRouter` debe envolver **por fuera** de `connect`: si solo hay `connect`, react-redux 5 puede hacer
// bail-out cuando el store no cambia y el subárbol (este `Switch`) no se vuelve a renderizar aunque la
// URL sí (p. ej. agenda → modificar contacto: loading + push, barra de direcciones ok, vista congelada).
const ConnectedV3PrivateShellSwitchInner = withRouter(
    connect((state) => ({
        featureSignatureDispatchControl: !!configSelectors.getConfigKey(
            state,
            "feature.signatureSchema.dispatchControl",
        ),
        isAdministrator: sessionSelectors.isAdministrator(state),
    }))(V3PrivateShellSwitchInner),
);

function buildCorporateCardDefinitions(activeEnvironment) {
    const show = config.getBoolean("frontend.show.corporateCard.summary", false);
    const perm = activeEnvironment?.permissions || {};
    const isRetail = activeEnvironment?.type === ENVIRONMENT_TYPE.RETAIL;
    const authorizedRoute = Boolean(show && perm["corporateCard.summary"] && !isRetail);

    return [
        {
            path: "/v2/corporateCard/accountStatements",
            authorizedRoute,
            component: AccountStatementsPage,
            componentProps: { embeddedInV3Shell: true },
        },
    ];
}

const FREQUENT_DESTINATION_COMPONENT_BY_PATH = {
    "/FrequentDestination/create/step1": FrequentDestinationCreate,
    "/frequentDestination/create/:id/confirm": FrequentDestinationCreateConfirmation,
    "/frequentDestination/:id/modify/confirmation": ModifyFrequentDestinationConfirmations,
    "/FrequentDestination/detail/:id/": FrequentDestinationDetail,
    "/transfer/frequentDestination/:id/create/": FrequentDestinationCreateTransfer,
    "/frequentDestination/:id/delete": FrequentDestinationDelete,
    "/frequentDestination/:id/modify": ModifyFrequentDestination,
    "/FrequentDestination": FrequentDestinationList,
};

function buildFrequentDestinationDefinitions(activeEnvironment) {
    const perm = activeEnvironment?.permissions;
    const canFrequentDestination = !!perm?.frequentDestination;
    const canManage = !!perm?.["frequentDestination.manage"];

    return V3_PRIVATE_SHELL_FREQUENT_PATH_RULES.map(({ path, rule }) => ({
        path,
        authorizedRoute: rule === "manage" ? canManage : canFrequentDestination,
        component: FREQUENT_DESTINATION_COMPONENT_BY_PATH[path],
    }));
}

function canAccessTransferHistoric(permissions) {
    return !!permissions?.transferAccess;
}

function canAccessV2TransferForms(permissions) {
    return (
        !!permissions?.transferInternal ||
        !!permissions?.transferThirdParties ||
        !!(permissions?.transferToContact && permissions?.frequentDestination)
    );
}

/**
 * Misma visibilidad que el ítem Autorizaciones del rail ({@link buildPrivateNavItems}).
 *
 * @param {object|null|undefined} activeEnvironment
 * @param {boolean} featureSignatureDispatchControl
 * @returns {boolean}
 */
function canAccessPendingAuthorizationsShell(activeEnvironment, featureSignatureDispatchControl) {
    if (!activeEnvironment) {
        return false;
    }
    if (activeEnvironment.type === CORPORATE_GROUP_ENVIRONMENT_TYPE) {
        return false;
    }
    return !!featureSignatureDispatchControl;
}
